import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { hasGeneratedPrompts } from "../lib/applicationCompleteness.mjs";
import { answerEntries, updateDraftAnswer } from "../lib/applicationAnswers.mjs";
import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { groupApplicationDrafts } from "../lib/applicationDraftHistory.mjs";

export default function AIApplications() {
  const [drafts, setDrafts] = useState([]);
  const [prefs, setPrefs] = useState({ preferred_countries: "", preferred_roles: "", preferred_work_types: [], minimum_salary: "", sponsorship_required: false, languages: "" });
  const [msg, setMsg] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const loadPending = useRef(false);
  const [savingId, setSavingId] = useState("");
  const actionPending = useRef(false);
  const preferencePending = useRef(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferenceMessage, setPreferenceMessage] = useState("");
  const [preferenceError, setPreferenceError] = useState("");
  const [submissionDraft, setSubmissionDraft] = useState(null);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const load = useCallback(async () => {
    if (loadPending.current) return;
    loadPending.current = true;
    setLoading(true);
    setLoadError("");
    try {
      const [p, d] = await Promise.all([api.get("/candidate/preferences"), api.get("/ai-applications/mine")]);
      setPrefs((current) => ({ ...current, ...p.data, preferred_countries: (p.data.preferred_countries || []).join(", "), preferred_roles: (p.data.preferred_roles || []).join(", "), languages: (p.data.languages || []).join(", ") }));
      setDrafts(d.data);
    } catch (e) {
      const status = e?.response?.status;
      setLoadError(status === 401 ? "Please sign in to use application assistance." : applicationErrorMessage(e, "Unable to load your application workspace. Please try again."));
    } finally {
      loadPending.current = false;
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePreferences = async () => {
    if (preferencePending.current) return;
    setPreferenceMessage("");
    setPreferenceError("");
    const salary = prefs.minimum_salary === "" || prefs.minimum_salary == null ? null : Number(prefs.minimum_salary);
    if (salary !== null && (!Number.isFinite(salary) || salary < 0)) {
      setPreferenceError("Enter a valid minimum salary of zero or more.");
      return;
    }
    preferencePending.current = true;
    setSavingPreferences(true);
    const list = (v) => v.split(",").map((x) => x.trim()).filter(Boolean);
    try {
      await api.put("/candidate/preferences", { ...prefs, preferred_countries: list(prefs.preferred_countries), preferred_roles: list(prefs.preferred_roles), languages: list(prefs.languages), minimum_salary: salary });
      setPreferenceMessage("Job preferences saved.");
    } catch (e) {
      setPreferenceError(applicationErrorMessage(e, "Unable to save job preferences. Please try again."));
    } finally {
      preferencePending.current = false;
      setSavingPreferences(false);
    }
  };
  const decide = async (id, decision) => {
    if (actionPending.current) return;
    if (decision === "approved" && hasGeneratedPrompts(drafts.find((item) => item.id === id)?.cover_letter)) {
      setMsg("Replace the bracketed guidance with your own details before approving this draft.");
      return;
    }
    actionPending.current = true;
    setSavingId(id);
    setMsg("");
    try {
      if (decision === "approved") {
        const draft = drafts.find((item) => item.id === id);
        await api.patch("/ai-applications/" + id, {
          cover_letter: draft.cover_letter, answers: draft.answers || {},
        });
      }
      const { data } = await api.post("/ai-applications/" + id + "/decision", { decision });
      setDrafts((items) => items.map((item) => item.id === id ? data : item));
      setMsg(decision === "approved" ? "Draft approved. Review it and use Submit approved application when ready." : "Draft rejected.");
    } catch (e) {
      setMsg(applicationErrorMessage(e, "Unable to update this draft. Please try again."));
    } finally { actionPending.current = false; setSavingId(""); }
  };
  const changeDraft = (id, cover_letter) => {
    setDrafts((items) => items.map((item) => item.id === id ? { ...item, cover_letter } : item));
  };
  const changeAnswer = (id, question, value) => {
    setDrafts((items) => updateDraftAnswer(items, id, question, value));
  };
  const saveDraft = async (draft) => {
    if (actionPending.current) return;
    actionPending.current = true;
    setSavingId(draft.id);
    try {
      const { data } = await api.patch("/ai-applications/" + draft.id, {
        cover_letter: draft.cover_letter, answers: draft.answers || {},
      });
      setDrafts((items) => items.map((item) => item.id === draft.id ? data : item));
      setMsg("Draft changes saved.");
    } catch (e) { setMsg(applicationErrorMessage(e, "Unable to save this draft.")); }
    finally { actionPending.current = false; setSavingId(""); }
  };
  const submitDraft = async () => {
    if (!submissionDraft || !submissionConfirmed || actionPending.current) return;
    if (hasGeneratedPrompts(submissionDraft.cover_letter)) {
      setSubmissionError("This letter still contains generated guidance. Prepare a new draft from the vacancy and complete it before submitting.");
      return;
    }
    const id = submissionDraft.id;
    setSubmissionError("");
    actionPending.current = true;
    setSavingId(id);
    try {
      const { data } = await api.post("/ai-applications/" + id + "/submit");
      setDrafts((items) => items.map((item) => item.id === id ? { ...item, status: data.status, application_id: data.application.id } : item));
      setSubmissionDraft(null);
      setSubmissionConfirmed(false);
      setMsg("Application submitted successfully. You can track it from your dashboard.");
    } catch (e) { setSubmissionError(applicationErrorMessage(e, "Unable to submit this application. Please try again.")); }
    finally { actionPending.current = false; setSavingId(""); }
  };

  const history = groupApplicationDrafts(drafts);

  if (loading || loadError) return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="ai-applications-page">
      <p className="overline">Assisted applications</p>
      <h1 className="mt-3 text-3xl font-semibold">AI application workspace</h1>
      {loading ? <p role="status" className="mt-4 text-sm text-slate-600">Loading your preferences and application drafts…</p> : <>
        <p role="alert" className="mt-4 text-sm text-red-700">{loadError}</p>
        <button className="btn btn-primary mt-4" onClick={load}>Retry loading workspace</button>
      </>}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="ai-applications-page">
      <p className="overline">Assisted applications</p>
      <h1 className="mt-3 text-3xl font-semibold">AI application workspace</h1>
      <p className="mt-3 text-sm text-slate-600">Prepare tailored drafts from your profile and a vacancy. You always review and approve content; nothing is submitted automatically.</p>
      <section className="card p-6 mt-8">
        <h2 className="text-lg font-semibold">Job preferences</h2>
        <div className="grid gap-3 md:grid-cols-2 mt-4">
          {[["preferred_countries", "Preferred countries (comma separated)"], ["preferred_roles", "Preferred roles (comma separated)"], ["languages", "Languages (comma separated)"]].map(([key, label]) => (
            <label key={key} className="block text-sm">{label}<input className="field mt-1" value={prefs[key] || ""} onChange={(e) => setPrefs({ ...prefs, [key]: e.target.value })} /></label>
          ))}
          <label className="block text-sm">Minimum salary<input className="field mt-1" type="number" value={prefs.minimum_salary ?? ""} onChange={(e) => setPrefs({ ...prefs, minimum_salary: e.target.value })} /></label>
        </div>
        <label className="flex gap-2 items-center text-sm mt-4"><input type="checkbox" checked={!!prefs.sponsorship_required} onChange={(e) => setPrefs({ ...prefs, sponsorship_required: e.target.checked })} /> Sponsorship required</label>
        <button onClick={savePreferences} disabled={savingPreferences} className="btn btn-primary mt-4">{savingPreferences ? "Saving preferences…" : "Save preferences"}</button>
        {preferenceMessage && <p role="status" className="mt-3 text-sm text-emerald-700">{preferenceMessage}</p>}
        {preferenceError && <p role="alert" className="mt-3 text-sm text-red-700">{preferenceError}</p>}
        {msg && <span className="ml-3 text-sm text-emerald-700">{msg}</span>}
      </section>
      <section className="card p-6 mt-6">
        <h2 className="text-lg font-semibold">Application draft history ({history.length})</h2>
        <div className="mt-4 space-y-4">
          {history.map((d) => <article key={d.id} className="border border-slate-200 rounded-xl p-4">
            <div className="flex flex-wrap justify-between gap-2"><strong>{d.job_title}</strong><span className="pill bg-slate-100 text-slate-700">{d.status}</span></div>
            <p className="text-sm text-slate-500">{d.company_name}</p>
            {d.status === "draft" ? <textarea className="field mt-3 min-h-64 w-full text-sm" disabled={!!savingId} value={d.cover_letter} onChange={(e) => changeDraft(d.id, e.target.value)} /> : <pre className="whitespace-pre-wrap text-sm mt-3 font-sans">{d.cover_letter}</pre>}
            {(d.status === "draft" || d.status === "approved") && hasGeneratedPrompts(d.cover_letter) && <p className="mt-3 text-sm text-amber-700">
              {d.status === "draft" ? "Replace the bracketed guidance with your own details before approval. You can still save your progress." : "This approved letter contains unfinished guidance. Open the vacancy and prepare a new draft to complete it."}
              {d.status === "approved" && d.job_id && <a className="underline ml-2" href={"/jobs/" + encodeURIComponent(d.job_id)}>Open vacancy</a>}
            </p>}
            {answerEntries(d.answers).length > 0 && <section className="mt-4">
              <h3 className="text-sm font-semibold">Application answers</h3>
              <div className="mt-3 space-y-3">
                {answerEntries(d.answers).map(([question, answer]) => d.status === "draft" ?
                  <label key={question} className="block text-sm">{question}
                    <textarea className="field mt-1 w-full" disabled={!!savingId} value={answer} onChange={(event) => changeAnswer(d.id, question, event.target.value)} />
                  </label> : <div key={question} className="text-sm">
                    <p className="font-medium">{question}</p><p className="mt-1 whitespace-pre-wrap">{answer || "No answer provided."}</p>
                  </div>)}
              </div>
            </section>}
            {d.status === "draft" && <div className="flex flex-wrap gap-2 mt-4"><button className="btn btn-ghost" disabled={!!savingId} onClick={() => saveDraft(d)}>Save changes</button><button className="btn btn-primary" disabled={!!savingId || hasGeneratedPrompts(d.cover_letter)} onClick={() => decide(d.id, "approved")}>Approve draft</button><button className="btn btn-ghost" disabled={!!savingId} onClick={() => decide(d.id, "rejected")}>Reject</button></div>}
            {d.status === "approved" && <div className="mt-4"><p className="text-xs text-amber-700">Draft approved. Review submission to open the final confirmation step.</p><button className="btn btn-primary mt-3" disabled={!!savingId} onClick={() => { setSubmissionConfirmed(false); setSubmissionError(""); setSubmissionDraft(d); }}>Review submission</button></div>}
            {d.status === "submitted" && <p className="mt-3 text-sm text-emerald-700">Submitted · Application ID: {d.application_id}</p>}
            {d.relatedDrafts.length > 0 && <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-slate-600">{d.relatedDrafts.length + 1} submitted drafts linked to this application · View earlier drafts</summary>
              <p className="mt-2 text-slate-500">These drafts share one application record.</p>
              {d.relatedDrafts.map((previous) => <div key={previous.id} className="mt-3 border-t pt-3">
                <p className="text-xs text-slate-500">Earlier draft · {previous.id}</p>
                <pre className="whitespace-pre-wrap font-sans mt-2">{previous.cover_letter}</pre>
                {answerEntries(previous.answers).map(([question, answer]) => <div key={question} className="mt-2"><p className="font-medium">{question}</p><p className="whitespace-pre-wrap">{answer || "No answer provided."}</p></div>)}
              </div>)}
            </details>}
          </article>)}
          {drafts.length === 0 && <p className="text-sm text-slate-500">Open a vacancy and choose “Prepare application draft” to create one.</p>}
        </div>
      </section>
      {submissionDraft && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5">
          <section role="dialog" aria-modal="true" aria-labelledby="submission-title" className="card bg-white p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <h2 id="submission-title" className="text-xl font-semibold">Confirm application submission</h2>
            <p className="mt-3 font-semibold">{submissionDraft.job_title}</p>
            <p className="text-sm text-slate-600">{submissionDraft.company_name}</p>
            <p className="mt-4 text-sm text-slate-700">Submitting creates an application in EuropEdu. Staff can review it and you can track it from your dashboard.</p>
            <pre className="mt-4 whitespace-pre-wrap text-sm font-sans border rounded-lg p-3">{submissionDraft.cover_letter}</pre>
            {answerEntries(submissionDraft.answers).length > 0 && <section className="mt-4 border rounded-lg p-3">
              <h3 className="text-sm font-semibold">Application answers</h3>
              {answerEntries(submissionDraft.answers).map(([question, answer]) => <div key={question} className="mt-3 text-sm"><p className="font-medium">{question}</p><p className="mt-1 whitespace-pre-wrap">{answer || "No answer provided."}</p></div>)}
            </section>}
            {hasGeneratedPrompts(submissionDraft.cover_letter) && <p role="alert" className="mt-4 text-sm text-amber-700">This letter contains unfinished guidance. Cancel, open the vacancy, and prepare a new draft to complete it.</p>}
            <label className="flex gap-3 items-start text-sm mt-5">
              <input type="checkbox" checked={submissionConfirmed} disabled={!!savingId} onChange={(event) => setSubmissionConfirmed(event.target.checked)} />
              I have reviewed this application and want to submit it.
            </label>
            {submissionError && <p role="alert" className="mt-4 text-sm text-red-700">{submissionError}</p>}
            <div className="mt-5 flex flex-wrap gap-3">
              <button autoFocus className="btn btn-ghost" disabled={!!savingId} onClick={() => { setSubmissionDraft(null); setSubmissionConfirmed(false); }}>Cancel</button>
              <button className="btn btn-primary disabled:opacity-50" disabled={!submissionConfirmed || !!savingId || hasGeneratedPrompts(submissionDraft.cover_letter)} onClick={submitDraft}>{savingId ? "Submitting…" : "Confirm and submit application"}</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
